import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MemoriaDeCalculoDto } from '../../dtos/memoria-de-calculo';

export interface LinhaDespesa {
  descricao: string;
  valor: number;
  percentual: number;
  isTitulo: boolean;
  nivelRecuo: number;
  isTotal: boolean;
}

// Fonte única das linhas da tabela: usada pela tela e pelos CSVs dos relatórios.
export function montarLinhasDetalhamentoDespesa(m: MemoriaDeCalculoDto | undefined, estiloProjecao: boolean): LinhaDespesa[] {
  const dist = m?.resultadoDistribuicaoDespesaPorEtapa;
  const dn = dist?.despesaNecessaria ?? 0;

  const pessoal = m?.resultadoDespesaPessoal?.despesaTotalPessoal ?? 0;
  const docentes = m?.resultadoDespesaRemuneracaoProfessor?.totalRemuneracaoDocente ?? 0;
  const subtotalPessoal = m?.resultadoDespesaRemuneracaoPessoal?.subtotal ?? 0;
  const funcionarios = Math.max(subtotalPessoal - docentes, 0);
  const adicionalRural = m?.resultadoDespesaAdicionalRural?.total ?? 0;
  const encargos = m?.resultadoDespesaEncargos?.valorTotalEncargosSociais ?? 0;
  const manutencao = m?.resultadoDespesaManutencao?.despesasManutencaoEOutros ?? 0;
  const itensManutencao = m?.resultadoDespesaManutencao?.itens ?? [];
  const alimentacao = m?.resultadoDespesaAlimentacao?.resultadoDespesaAlimentacaoTotal ?? 0;

  const indigena = dist?.despesaFatorIndigena ?? 0;
  const quilombola = dist?.despesaFatorQuilombola ?? 0;
  const aee = dist?.despesaFatorAee ?? 0;
  const ensMedioProf = dist?.despesaFatorEnsMedioProfissional ?? 0;
  const ejaProf = dist?.despesaFatorEjaProfissional ?? 0;
  const adicionalModalidades = indigena + quilombola + aee + ensMedioProf + ejaProf;

  const linhas: LinhaDespesa[] = [];
  const add = (descricao: string, valor: number, isTitulo = false, nivelRecuo = 0, isTotal = false) =>
    linhas.push({ descricao, valor, percentual: dn > 0 ? (valor / dn) * 100 : 0, isTitulo, nivelRecuo, isTotal });

  // Pessoal
  add('Pessoal', pessoal, true, 0);
  add('Docentes', docentes, false, 1);
  add('Funcionários das escolas (não docentes)', funcionarios, false, 1);
  // Auxiliares docentes -> ocultar por enquanto (spec #1094)
  add('Adicional de remuneração para profissionais da Educação Escolar do campo', adicionalRural, false, 1);
  add('Encargos sociais (Previdência social - parte patronal)', encargos, false, 1);

  // Serviços, infraestrutura, Recursos e Despesas Administrativas (itens editados pelo usuário)
  add('Serviços, infraestrutura, Recursos e Despesas Administrativas', manutencao, true, 0);
  itensManutencao.forEach((item) => add(item.descricao, item.valor, false, 1));

  // Alimentação escolar
  add('Alimentação escolar (gêneros alimentícios e outros insumos)', alimentacao, true, 0);

  // Adicional de recursos para modalidades e contextos (exibe todas as modalidades, mesmo zeradas)
  add('Adicional de recursos para modalidades e contextos específicos', adicionalModalidades, true, 0);
  add('Educação Escolar Indígena', indigena, false, 1);
  add('Educação Escolar Quilombola', quilombola, false, 1);
  add('Atendimento Educacional Especializado (AEE)', aee, false, 1);
  add('Educação Profissional Integrada e concomitante ao Ensino Médio', ensMedioProf, false, 1);
  add('Educação Profissional Integrada e concomitante à EJA', ejaProf, false, 1);

  // Total geral (no rodapé) — R2 (projeção) usa "Total"; R3/R4 usam o nome completo
  add(estiloProjecao ? 'Total' : 'Despesa necessária para financiar o CAQ', dn, true, 0, true);

  return linhas;
}

@Component({
  selector: 'app-tabela-detalhamento-despesa',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabela-detalhamento-despesa.component.html',
  styleUrl: './tabela-detalhamento-despesa.component.scss',
})
export class TabelaDetalhamentoDespesaComponent {
  @Input() memoria!: MemoriaDeCalculoDto;
  @Input() estiloProjecao = false;
  @Output() downloadClick = new EventEmitter<void>();

  get linhas(): LinhaDespesa[] {
    return montarLinhasDetalhamentoDespesa(this.memoria, this.estiloProjecao);
  }
}
